<script setup>
import { computed, onMounted, onUnmounted, watch, nextTick, ref } from 'vue';
import { useSidebarContext, usePopoverState } from './provider';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import Icon from 'next/icon/Icon.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarSubGroup from './SidebarSubGroup.vue';
import SidebarGroupEmptyLeaf from './SidebarGroupEmptyLeaf.vue';
import SidebarCollapsedPopover from './SidebarCollapsedPopover.vue';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
  scopeInboxId: { type: [String, Number], default: null },
  unreadCount: { type: Number, default: 0 },
});

const {
  expandedItem,
  setExpandedItem,
  resolvePath,
  resolvePermissions,
  resolveFeatureFlag,
  isAllowed,
  isCollapsed,
  isResizing,
} = useSidebarContext();

const {
  activePopover,
  setActivePopover,
  closeActivePopover,
  scheduleClose,
  cancelClose,
} = usePopoverState();

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const isExpanded = computed(() => expandedItem.value === props.name);
const isExpandable = computed(() => props.children);
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

// Use shared popover state - only one popover can be open at a time
const isPopoverOpen = computed(() => activePopover.value === props.name);
const triggerRef = ref(null);
const triggerRect = ref({ top: 0, left: 0, bottom: 0, right: 0 });

const openPopover = () => {
  if (triggerRef.value) {
    const rect = triggerRef.value.getBoundingClientRect();
    triggerRect.value = {
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
    };
  }
  setActivePopover(props.name);
};

const closePopover = () => {
  if (activePopover.value === props.name) {
    closeActivePopover();
  }
};

const handleMouseEnter = () => {
  if (!hasChildren.value || isResizing.value) return;
  cancelClose();
  openPopover();
};

const handleMouseLeave = () => {
  if (!hasChildren.value) return;
  scheduleClose(200);
};

const handlePopoverMouseEnter = () => {
  cancelClose();
};

const handlePopoverMouseLeave = () => {
  scheduleClose(100);
};

// Close popover when mouse leaves the window
const handleWindowBlur = () => {
  closeActivePopover();
};

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  // Walk into subgroups: a subgroup header has no link of its own, so without
  // this an inbox whose first entry is "Teams" would jump straight to Mentions.
  return navigableChildren.value.filter(
    child => child.to && isAllowed(child.to)
  );
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0;
});

const matchesInboxScope = () => {
  if (props.scopeInboxId == null) return true;
  return String(route.params.inbox_id) === String(props.scopeInboxId);
};

const paramsMatch = (child, extraParams = []) => {
  if (!child.to?.params) return false;
  const keys = [...Object.keys(child.to.params), ...extraParams];
  return keys.every(
    key =>
      child.to.params[key] != null &&
      String(child.to.params[key]) === String(route.params[key])
  );
};

const pathMatches = (childPath, currentPath) =>
  currentPath === childPath || currentPath.startsWith(`${childPath}/`);

const isActive = computed(() => {
  if (!matchesInboxScope()) return false;

  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;

    return props.activeOn.includes(route.name);
  }

  return false;
});

const activeChild = computed(() => {
  if (!matchesInboxScope()) return undefined;

  const currentPath = route.path;

  const pathMatchesList = navigableChildren.value.filter(child => {
    if (!child.to) return false;
    return pathMatches(resolvePath(child.to), currentPath);
  });

  if (pathMatchesList.length) {
    return pathMatchesList.sort(
      (a, b) => resolvePath(b.to).length - resolvePath(a.to).length
    )[0];
  }

  const activeOnPages = navigableChildren.value.filter(
    child => child.to && child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    return activeOnPages.find(child => paramsMatch(child));
  }

  return undefined;
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const resolvedUnreadCount = computed(() => {
  const value = props.unreadCount || 0;
  if (!value) return '';
  return value < 100 ? `${value}` : '99+';
});

const navigateToPrimaryChild = () => {
  if (!hasAccessibleChildren.value) return;
  // Prefer "Todas as conversas" when present; otherwise first accessible child.
  const allItem = accessibleItems.value.find(
    item =>
      String(item.name || '').startsWith('InboxAll-') ||
      item.to?.name === 'inbox_dashboard'
  );
  const target = allItem || accessibleItems.value[0];
  if (target?.to) router.push(target.to);
};

const handleCollapsedClick = () => {
  if (hasChildren.value && hasAccessibleChildren.value) {
    navigateToPrimaryChild();
  }
};

const toggleTrigger = () => {
  // Always navigate to the channel list (Todas) when the channel header is clicked,
  // even if already expanded / has an active child — fixes intermittent "no open".
  if (hasAccessibleChildren.value) {
    navigateToPrimaryChild();
  }
  setExpandedItem(props.name);
};

onMounted(async () => {
  await nextTick();
  if (hasActiveChild.value) {
    expandedItem.value = props.name;
  }
  window.addEventListener('blur', handleWindowBlur);
  document.addEventListener('mouseleave', handleWindowBlur);
});

onUnmounted(() => {
  window.removeEventListener('blur', handleWindowBlur);
  document.removeEventListener('mouseleave', handleWindowBlur);
});

watch(
  () => [route.name, route.params.inbox_id, route.params.teamId],
  () => {
    if (hasActiveChild.value) {
      expandedItem.value = props.name;
    }
  },
  { immediate: true }
);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="grid gap-1 text-sm cursor-pointer select-none min-w-0"
  >
    <!-- Collapsed State -->
    <template v-if="isCollapsed">
      <div
        class="relative"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <component
          :is="to && !hasChildren ? 'router-link' : 'button'"
          ref="triggerRef"
          :to="to && !hasChildren ? to : undefined"
          type="button"
          class="flex items-center justify-center size-10 rounded-lg"
          :class="{
            'text-n-slate-12 bg-[#6ca9f9]': isActive || hasActiveChild,
            'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
          }"
          :title="label"
          @click="hasChildren ? handleCollapsedClick() : undefined"
        >
          <Icon v-if="icon" :icon="icon" :class="typeof icon === 'string' ? 'size-4' : 'size-[1.9em]'" />
        </component>
        <span
          v-if="resolvedUnreadCount"
          class="pointer-events-none min-h-[14px] min-w-[14px] px-0.5 bg-n-ruby-9 rounded-full absolute top-0 ltr:right-0 rtl:left-0 z-10 grid place-items-center text-[9px] leading-none text-white font-semibold"
        >
          {{ resolvedUnreadCount }}
        </span>
        <SidebarCollapsedPopover
          v-if="hasChildren && isPopoverOpen"
          :label="label"
          :children="children"
          :active-child="activeChild"
          :trigger-rect="triggerRect"
          @close="closePopover"
          @mouseenter="handlePopoverMouseEnter"
          @mouseleave="handlePopoverMouseLeave"
        />
      </div>
    </template>
    <!-- Expanded State -->
    <template v-else>
      <SidebarGroupHeader
        :icon
        :name
        :label
        :to
        :getter-keys="getterKeys"
        :unread-count="unreadCount"
        :is-active="isActive"
        :has-active-child="hasActiveChild"
        :expandable="hasChildren"
        :is-expanded="isExpanded"
        @toggle="toggleTrigger"
      />
      <ul
        v-if="hasChildren"
        v-show="isExpanded"
        class="grid m-0 list-none sidebar-group-children min-w-0"
      >
        <template v-for="child in children" :key="child.name">
          <SidebarSubGroup
            v-if="child.children"
            :label="child.label"
            :icon="child.icon"
            :children="child.children"
            :is-expanded="isExpanded"
            :active-child="activeChild"
          />
          <SidebarGroupLeaf
            v-else-if="isAllowed(child.to)"
            v-show="isExpanded || activeChild?.name === child.name"
            v-bind="child"
            :active="activeChild?.name === child.name"
          />
        </template>
      </ul>
      <ul v-else-if="isExpandable && isExpanded">
        <SidebarGroupEmptyLeaf />
      </ul>
    </template>
  </Policy>
</template>

<style>
.sidebar-group-children .child-item::before {
  content: '';
  position: absolute;
  width: 0.125rem;
  /* 0.5px */
  height: 100%;
}

.sidebar-group-children .child-item:first-child::before {
  border-radius: 4px 4px 0 0;
}

/* This selects the last child in a group */
/* https://codepen.io/scmmishra/pen/yLmKNLW */
.sidebar-group-children > .child-item:last-child::before,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::before {
  height: 20%;
}

.sidebar-group-children > .child-item:last-child::after,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 12px;
  bottom: calc(50% - 2px);
  border-bottom-width: 0.125rem;
  border-left-width: 0.125rem;
  border-right-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 0 4px;
  left: 0;
}

#app[dir='rtl'] .sidebar-group-children > .child-item:last-child::after,
#app[dir='rtl']
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  right: 0;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  border-left-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 4px 0px;
}
</style>
